import {SpanIcon} from '@mazraeapp/react_icon_kit';
import {Dispatch, SetStateAction, useCallback, useMemo, useState} from 'react';
import {BottomSheet} from 'react-spring-bottom-sheet';
import AutoResizeTextarea from '../../../../../../components/AutoResizeTextarea';
import Button from '../../../../../../components/button';
import CustomRadio from '../../../../../../components/mazraeKit/customRadio';
import usePostTerminateAgreement from '../../../../../../services/hookService/postTerminateAgreement/usePostTerminateAgreement';

type IProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  agreementGuid?: string;
  onSuccess?: () => void;
  onShowSuccessBottomSheet?: () => void;
};

type optionTypes = 'closed' | 'bankrupted' | 'unsatisfied' | 'other';

interface IReasonList {
  title: string;
  value: optionTypes;
}

const reasonList: IReasonList[] = [
  {title: 'تعطیل کردن کسب و کار فروشگاهی', value: 'closed'},
  {title: 'ورشکستگی مالی و کفایت موجودی', value: 'bankrupted'},
  {title: 'عدم رضایت در اجرای مفاد قرارداد', value: 'unsatisfied'},
  {title: 'دلایل دیگر', value: 'other'},
];

const CancelAgreementBottomSheet = ({
  open,
  setOpen,
  agreementGuid,
  onSuccess,
  onShowSuccessBottomSheet,
}: IProps) => {
  const [selectedOption, setSelectedOption] = useState<optionTypes | null>(
    null,
  );
  const [showReasonInput, setShowReasonInput] = useState<boolean>(false);
  const [reasonValue, setReasonValue] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTimeout(() => {
      setSelectedOption(null);
      setShowReasonInput(false);
      setReasonValue(null);
    }, 300);
  }, [setOpen]);

  const handleSuccess = useCallback(() => {
    handleClose();
    onShowSuccessBottomSheet?.();
    onSuccess?.();
  }, [handleClose, onShowSuccessBottomSheet, onSuccess]);

  const {mutate, isLoading} = usePostTerminateAgreement({
    guid: agreementGuid!,
    termination_reason: reasonValue!,
    onSuccess: handleSuccess,
  });

  const clickRadioHandler = useCallback((value: optionTypes) => {
    setSelectedOption(value);
    setShowReasonInput(value === 'other');
    if (value !== 'other') {
      setReasonValue(null);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!agreementGuid) {
      console.error('Agreement GUID is required');
      return;
    }
    if (!selectedOption) {
      return;
    }
    mutate();
  }, [agreementGuid, selectedOption, mutate]);

  const isOtherSelected = selectedOption === 'other';
  const isCancelButtonDisabled = useMemo(() => {
    return (
      !selectedOption || (isOtherSelected && !reasonValue?.trim()) || isLoading
    );
  }, [selectedOption, isOtherSelected, reasonValue, isLoading]);

  return (
    <BottomSheet
      open={open}
      onDismiss={handleClose}
      blocking
      className="custom-bottom-sheet"
    >
      <div className="relative flex flex-col gap-2 px-4 py-4">
        <SpanIcon
          className="absolute text-2xl text-gray-600 cursor-pointer top-4 left-5 icon-nameClose-remove"
          onClick={handleClose}
        />

        <h4 className="text-sm font-medium text-center text-gray-700 mt-2.5">
          درخواست فسخ قرارداد
        </h4>

        <div
          className={`flex flex-col justify-center gap-[1rem] ${
            !isOtherSelected && 'mb-4'
          }`}
        >
          <h6 className="mt-4 mb-2 text-gray-500 text-md">
            دلیل فسخ قرارداد را مشخص کنید
          </h6>

          {reasonList.map(item => (
            <CustomRadio
              key={item.value}
              name={item.value}
              value={item.value}
              title={item.title}
              checked={item.value === selectedOption}
              onChange={() => clickRadioHandler(item.value)}
              customClassName="font-medium text-sm text-gray-800"
              disabled={isLoading}
            />
          ))}

          {showReasonInput && (
            <div>
              <h5 className="text-sm text-zinc-500">دلیلتان را بنویسید</h5>
              <AutoResizeTextarea
                value={reasonValue ?? ''}
                onChange={e => setReasonValue(e.target.value)}
                placeholder="دلیل فسخ قراردادتان را وارد کنید"
                className="leading-10 min-h-10"
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        <Button
          className="flex items-center w-full gap-2 text-white border-none outline-none bg-farm-shop-500 disabled:opacity-50 disabled:bg-gray-400 disabled:text-white disabled:cursor-not-allowed"
          disabled={isCancelButtonDisabled}
          onClick={handleSubmit}
          isLoading={isLoading}
        >
          ثبت
        </Button>
      </div>
    </BottomSheet>
  );
};

export default CancelAgreementBottomSheet;
